import React, { useState, useEffect, useMemo } from 'react';
import {
  Euro, TrendingUp, Fuel, Wrench, Shield,
  Car, Building, Download, BarChart3, PieChart,
  ChevronDown, ChevronUp, AlertCircle, ArrowUpRight, ArrowDownRight,
  LayoutDashboard, Activity, CreditCard, Globe, Building2
} from 'lucide-react';
import { useCostKPIs, useCostsByVehicle, useCostsByOrg } from '../../hooks/useKPIs.js';
import { fuelCardAPI } from '../../api/operations.js';
import { useTabPermissions } from '../../v3/hooks/useTabPermissions.js';
import { useAuth } from '../../v3/context/AuthContext.jsx';
import { formatMoney } from '../../utils/formatMoney.js';
import { useFormat } from '../../i18n/format.js';
import SkeletonLoader from '../common/SkeletonLoader.jsx';

/**
 * CostDashboardView - Finance-focused view with TCO analysis
 * Based on GDPR tab design pattern with horizontal tabs
 *
 * i18n : tous les libellés vivent dans le namespace `costs` (fr.json / en.json).
 * L'argent passe par utils/formatMoney (locale déduite de la devise — voulu) ;
 * pourcentages et nombres suivent la langue de l'UI via useFormat().
 */

// Money formatting: ONE generic tool (utils/formatMoney), currency comes from the data.
const money = (v, currency) => formatMoney(v, currency || 'EUR');
const moneyInt = (v, currency) => formatMoney(v, currency || 'EUR', { decimals: 0 });

// Tab definitions - matching GDPR pattern (labels resolved at render: costs.tabs.<key>)
const COST_TABS = [
  { key: 'overview', icon: LayoutDashboard },
  { key: 'breakdown', icon: PieChart },
  { key: 'vehicles', icon: Car },
  { key: 'by_org', icon: Building2, groupOnly: true },
  { key: 'trends', icon: TrendingUp },
  { key: 'alerts', icon: AlertCircle },
];
const TAB_LABEL_KEYS = { overview: 'overview', breakdown: 'breakdown', vehicles: 'vehicles', by_org: 'byOrg', trends: 'trends', alerts: 'alerts' };

// Period label key (costs.periods.<key>) from the period code
const PERIOD_KEYS = { month: 'thisMonth', quarter: 'thisQuarter', year: 'thisYear' };

// Cost family label: common.costFamilies.<key> (fallback = raw key)
const familyLabel = (t, key) => t(`common.costFamilies.${key}`, { defaultValue: key });

// Status Card Component - matching GDPR ComplianceCard pattern
function StatusCard({ icon: Icon, title, value, status, description, trend, trendValue }) {
  const statusColors = {
    good: 'text-green-600 bg-green-50',
    warning: 'text-amber-600 bg-amber-50',
    danger: 'text-red-600 bg-red-50',
    neutral: 'text-blue-600 bg-blue-50'
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-6 h-6 ${statusColors[status].split(' ')[0]}`} />
        <div className="flex items-center gap-2">
          {trend && (
            <span className={`flex items-center text-sm ${trend === 'up' ? 'text-red-600' : 'text-green-600'}`}>
              {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {trendValue}
            </span>
          )}
          <span className={`text-2xl font-bold ${statusColors[status].split(' ')[0]}`}>
            {value}
          </span>
        </div>
      </div>
      <h4 className="font-medium text-gray-900">{title}</h4>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  );
}

// Quick Action Button - matching GDPR pattern
function QuickActionButton({ icon: Icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-amber-50 hover:border-amber-300 transition-colors"
    >
      <Icon className="w-6 h-6 text-amber-600 mb-2" />
      <span className="text-sm text-gray-700">{label}</span>
    </button>
  );
}

// Fuel Card Consumption Widget — shows cards nearing their monthly limit
function FuelCardConsumptionWidget() {
  const { t, fmt } = useFormat();
  const [consumption, setConsumption] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fuelCardAPI.getConsumption()
      .then(res => setConsumption(res?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const withLimit = consumption.filter(c => c.monthly_limit > 0);
  if (withLimit.length === 0) return null;

  const totalLimit = withLimit.reduce((s, c) => s + c.monthly_limit, 0);
  const totalConsumed = withLimit.reduce((s, c) => s + (c.consumed || 0), 0);
  const globalPct = totalLimit > 0 ? Math.round((totalConsumed / totalLimit) * 100) : 0;
  const globalColor = globalPct >= 90 ? 'text-red-600' : globalPct >= 70 ? 'text-orange-600' : 'text-green-600';
  const barColor = globalPct >= 90 ? 'bg-red-500' : globalPct >= 70 ? 'bg-orange-400' : globalPct >= 50 ? 'bg-yellow-400' : 'bg-green-500';

  // Cards sorted by consumption % descending, show top alerts first
  const sorted = [...withLimit].sort((a, b) => (b.consumed_pct || 0) - (a.consumed_pct || 0));
  const alerts = sorted.filter(c => (c.consumed_pct || 0) >= 70);

  const currency = consumption.find(c => c.currency)?.currency || 'EUR';

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-blue-600" />
          {t('costs.fuelCards.title')}
        </h3>
        <span className={`text-2xl font-bold ${globalColor}`}>{fmt.percent(globalPct / 100, { decimals: 0 })}</span>
      </div>

      {/* Global gauge */}
      <div className="mb-1 flex justify-between text-xs text-gray-500">
        <span>{t('costs.fuelCards.consumed', { amount: money(totalConsumed, currency) })}</span>
        <span>{t('costs.fuelCards.totalLimit', { amount: money(totalLimit, currency) })}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
        <div className={`${barColor} h-3 rounded-full transition-all`} style={{ width: `${Math.min(globalPct, 100)}%` }} />
      </div>

      {/* Alert cards */}
      {alerts.length > 0 ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            <AlertCircle className="w-4 h-4 inline text-orange-500 mr-1" />
            {t('costs.fuelCards.nearLimit', { count: alerts.length })}
          </p>
          {alerts.slice(0, 5).map(c => {
            const pct = c.consumed_pct || 0;
            const col = pct >= 90 ? 'bg-red-500' : 'bg-orange-400';
            return (
              <div key={c.card_id} className="flex items-center gap-3">
                <span className="font-mono text-xs w-36 truncate text-gray-600">{c.card_number}</span>
                <div className="flex-1">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div className={`${col} h-1.5 rounded-full`} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                </div>
                <span className="text-xs font-semibold w-10 text-right" style={{ color: pct >= 90 ? '#dc2626' : '#ea580c' }}>{fmt.percent(pct / 100, { decimals: 0 })}</span>
                <span className="text-xs text-gray-400 w-20 text-right">{money(c.consumed, currency)}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-green-600">{t('costs.fuelCards.allWithinLimits')}</p>
      )}
    </div>
  );
}

// Cost Breakdown Bar
function CostBreakdownBar({ data, total, currency }) {
  const { t, fmt } = useFormat();
  const colors = {
    fuel: 'bg-green-500',
    maintenance: 'bg-orange-500',
    claims: 'bg-red-500',
    insurance: 'bg-blue-500',
    leasing: 'bg-purple-500',
    tax: 'bg-gray-500',
    other: 'bg-gray-400'
  };

  return (
    <div className="space-y-3">
      <div className="h-8 rounded-lg overflow-hidden flex">
        {Object.entries(data).filter(([, v]) => v > 0).map(([key, value]) => (
          <div
            key={key}
            className={`${colors[key]} h-full transition-all`}
            style={{ width: `${(value / total) * 100}%` }}
            title={t('costs.breakdown.segmentTooltip', { family: familyLabel(t, key), amount: money(value, currency) })}
          />
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {Object.entries(data).filter(([, v]) => v > 0).map(([key, value]) => (
          <div key={key} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded ${colors[key]}`} />
            <span className="text-xs text-gray-600">{familyLabel(t, key)}</span>
            <span className="text-xs font-bold text-gray-800">{fmt.percent(value / total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Vehicle Cost Row
function VehicleCostRow({ vehicle, costs, fleetAverage, expanded, onToggle, currency }) {
  const { t, fmt } = useFormat();
  const total = Object.values(costs).reduce((sum, v) => sum + v, 0);
  const vsAverage = fleetAverage > 0 ? ((total - fleetAverage) / fleetAverage) * 100 : 0;

  return (
    <>
      <tr
        onClick={onToggle}
        className="hover:bg-gray-50 cursor-pointer border-b"
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <Car className="w-5 h-5 text-gray-400" />
            <div>
              <p className="font-medium text-gray-900">{vehicle.license_plate}</p>
              <p className="text-xs text-gray-500">{vehicle.make} {vehicle.model}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-right">
          <span className="font-bold text-gray-900">{money(total, currency)}</span>
        </td>
        <td className="px-4 py-3 text-right">
          <span className={`px-2 py-1 rounded text-xs font-medium ${vsAverage > 10 ? 'bg-red-100 text-red-800' :
              vsAverage < -10 ? 'bg-green-100 text-green-800' :
                'bg-gray-100 text-gray-800'
            }`}>
            {vsAverage > 0 ? '+' : ''}{fmt.percent(vsAverage / 100)}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan="5" className="px-4 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Fuel className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-gray-500">{t('common.costFamilies.fuel')}</span>
                </div>
                <p className="font-bold text-gray-900">{money(costs.fuel || 0, currency)}</p>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Wrench className="w-4 h-4 text-orange-600" />
                  <span className="text-xs text-gray-500">{t('common.costFamilies.maintenance')}</span>
                </div>
                <p className="font-bold text-gray-900">{money(costs.maintenance || 0, currency)}</p>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="w-4 h-4 text-red-600" />
                  <span className="text-xs text-gray-500">{t('common.costFamilies.claims')}</span>
                </div>
                <p className="font-bold text-gray-900">{money(costs.claims || 0, currency)}</p>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Euro className="w-4 h-4 text-gray-600" />
                  <span className="text-xs text-gray-500">{t('common.costFamilies.other')}</span>
                </div>
                <p className="font-bold text-gray-900">{money(costs.other || 0, currency)}</p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Overview Tab
function OverviewTab({ costData, period, setActiveTab, handleExportCSV }) {
  const { t, fmt } = useFormat();
  const currency = costData.currency;
  const shareOfTotal = (v) => fmt.percent(costData.grandTotal > 0 ? v / costData.grandTotal : 0);
  return (
    <div className="space-y-6">
      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatusCard
          icon={Euro}
          title={t('costs.overview.totalFleet')}
          value={`${money(costData.grandTotal, currency)}`}
          status="neutral"
          description={t('costs.common.vehicles', { count: costData.vehicleCount })}
          trend={costData.trendPercent > 0 ? 'up' : 'down'}
          trendValue={fmt.percent(Math.abs(costData.trendPercent) / 100)}
        />
        <StatusCard
          icon={Car}
          title={t('costs.overview.avgPerVehicle')}
          value={`${moneyInt(costData.avgPerVehicle, currency)}`}
          status="neutral"
          description={t('costs.overview.period', { period: t(`costs.periods.${PERIOD_KEYS[period] || 'thisYear'}`) })}
        />
        <StatusCard
          icon={Fuel}
          title={t('common.costFamilies.fuel')}
          value={`${money(costData.totals.fuel, currency)}`}
          status={costData.totals.fuel > costData.grandTotal * 0.4 ? "warning" : "good"}
          description={t('costs.overview.shareOfTotal', { percent: shareOfTotal(costData.totals.fuel) })}
        />
        <StatusCard
          icon={Wrench}
          title={t('common.costFamilies.maintenance')}
          value={`${money(costData.totals.maintenance, currency)}`}
          status={costData.totals.maintenance > costData.grandTotal * 0.3 ? "warning" : "good"}
          description={t('costs.overview.shareOfTotal', { percent: shareOfTotal(costData.totals.maintenance) })}
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">{t('costs.overview.quickActions')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickActionButton
            icon={PieChart}
            label={t('costs.tabs.breakdown')}
            onClick={() => setActiveTab('breakdown')}
          />
          <QuickActionButton
            icon={Car}
            label={t('costs.vehicles.title')}
            onClick={() => setActiveTab('vehicles')}
          />
          <QuickActionButton
            icon={TrendingUp}
            label={t('costs.tabs.trends')}
            onClick={() => setActiveTab('trends')}
          />
          <QuickActionButton
            icon={Download}
            label={t('costs.overview.exportCsv')}
            onClick={handleExportCSV}
          />
        </div>
      </div>

      {/* Fuel Card Consumption */}
      <FuelCardConsumptionWidget />

      {/* Cost Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2 text-amber-600" />
          {t('costs.overview.byCategory')}
        </h3>
        <CostBreakdownBar data={costData.totals} total={costData.grandTotal || 1} currency={costData.currency} />

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mt-6">
          {Object.entries(costData.totals).map(([key, value]) => {
            const icons = {
              fuel: Fuel, maintenance: Wrench, claims: Shield, insurance: Euro,
              leasing: Car, tax: Building, other: Euro
            };
            const colors = {
              fuel: 'text-green-600', maintenance: 'text-orange-600', claims: 'text-red-600',
              insurance: 'text-blue-600', leasing: 'text-purple-600', tax: 'text-gray-600', other: 'text-gray-400'
            };
            const Icon = icons[key] || Euro;

            return (
              <div key={key} className="text-center p-4 bg-gray-50 rounded-lg">
                <Icon className={`w-6 h-6 mx-auto mb-2 ${colors[key] || 'text-gray-400'}`} />
                <p className="text-lg font-bold text-gray-900">{money(value, currency)}</p>
                <p className="text-xs text-gray-500">{familyLabel(t, key)}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Alerts */}
      {costData.sortedVehicles.some(v => v.total > costData.avgPerVehicle * 1.5) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-amber-600 mr-3 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-800">{t('costs.overview.watchTitle')}</h4>
              <ul className="mt-2 text-sm text-amber-700 space-y-1">
                {costData.sortedVehicles
                  .filter(v => v.total > costData.avgPerVehicle * 1.5)
                  .slice(0, 3)
                  .map(v => (
                    <li key={v.vehicle.id}>
                      {t('costs.overview.watchLine', {
                        plate: v.vehicle.license_plate,
                        amount: money(v.total, currency),
                        percent: fmt.percent((v.total - costData.avgPerVehicle) / costData.avgPerVehicle, { decimals: 0 }),
                      })}
                    </li>
                  ))}
              </ul>
              <button
                onClick={() => setActiveTab('alerts')}
                className="mt-2 text-amber-800 font-medium hover:underline"
              >
                {t('costs.overview.seeAllWatch')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Breakdown Tab - TCO Analysis
function BreakdownTab({ costData }) {
  const { t, fmt } = useFormat();
  const currency = costData.currency;
  const icons = {
    fuel: Fuel, maintenance: Wrench, claims: Shield, insurance: Euro,
    leasing: Car, tax: Building, other: Euro
  };
  const colors = {
    fuel: 'bg-green-50 border-green-200 text-green-600',
    maintenance: 'bg-orange-50 border-orange-200 text-orange-600',
    claims: 'bg-red-50 border-red-200 text-red-600',
    insurance: 'bg-blue-50 border-blue-200 text-blue-600',
    leasing: 'bg-purple-50 border-purple-200 text-purple-600',
    tax: 'bg-gray-50 border-gray-200 text-gray-600',
    other: 'bg-gray-50 border-gray-200 text-gray-400'
  };

  const sortedCategories = Object.entries(costData.totals)
    .sort(([, a], [, b]) => b - a);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <PieChart className="w-5 h-5 mr-2 text-amber-600" />
          {t('costs.breakdown.title')}
        </h3>
        <CostBreakdownBar data={costData.totals} total={costData.grandTotal || 1} currency={costData.currency} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedCategories.map(([key, value]) => {
          const Icon = icons[key] || Euro;
          const percentage = costData.grandTotal > 0 ? value / costData.grandTotal : 0;
          const perVehicle = costData.vehicleCount > 0 ? Math.round(value / costData.vehicleCount) : 0;

          return (
            <div key={key} className={`rounded-lg border p-4 ${colors[key] || 'bg-gray-50 border-gray-200 text-gray-600'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-6 h-6" />
                  <span className="font-semibold text-gray-900">{familyLabel(t, key)}</span>
                </div>
                <span className="text-sm font-medium">{fmt.percent(percentage)}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{money(value, currency)}</p>
              <p className="text-sm text-gray-500 mt-1">{t('costs.breakdown.perVehicle', { amount: moneyInt(perVehicle, currency) })}</p>
            </div>
          );
        })}
      </div>

      {/* TCO per vehicle */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Car className="w-5 h-5 mr-2 text-amber-600" />
          {t('costs.breakdown.tcoPerVehicle')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-amber-50 rounded-lg">
            <p className="text-2xl font-bold text-amber-600">
              {moneyInt(costData.avgPerVehicle, currency)}
            </p>
            <p className="text-sm text-amber-700">{t('costs.breakdown.tcoAvg')}</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">
              {costData.sortedVehicles.length > 0
                ? moneyInt(Math.min(...costData.sortedVehicles.map(v => v.total)), currency)
                : money(0, currency)}
            </p>
            <p className="text-sm text-green-700">{t('costs.breakdown.tcoMin')}</p>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">
              {costData.sortedVehicles.length > 0
                ? moneyInt(Math.max(...costData.sortedVehicles.map(v => v.total)), currency)
                : money(0, currency)}
            </p>
            <p className="text-sm text-red-700">{t('costs.breakdown.tcoMax')}</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{fmt.number(costData.vehicleCount)}</p>
            <p className="text-sm text-blue-700">{t('costs.common.vehiclesLabel')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Vehicles Tab - Cost per vehicle
function VehiclesTab({ costData }) {
  const { t } = useFormat();
  const [expandedVehicle, setExpandedVehicle] = useState(null);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">{t('costs.vehicles.title')}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <BarChart3 className="w-4 h-4" />
              {t('costs.vehicles.sortedHint')}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{t('costs.vehicles.colVehicle')}</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">{t('costs.vehicles.colTotal')}</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">{t('costs.vehicles.colVsAverage')}</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{t('costs.vehicles.colDetails')}</th>
              </tr>
            </thead>
            <tbody>
              {costData.sortedVehicles.slice(0, 20).map(v => (
                <VehicleCostRow
                  key={v.vehicle.id}
                  vehicle={v.vehicle}
                  costs={{
                    fuel: v.fuel,
                    maintenance: v.maintenance,
                    claims: v.claims,
                    other: v.insurance + v.leasing + v.tax + v.other
                  }}
                  fleetAverage={costData.avgPerVehicle}
                  currency={costData.currency}
                  expanded={expandedVehicle === v.vehicle.id}
                  onToggle={() => setExpandedVehicle(expandedVehicle === v.vehicle.id ? null : v.vehicle.id)}
                />
              ))}
            </tbody>
          </table>
        </div>

        {costData.sortedVehicles.length > 20 && (
          <div className="p-4 text-center text-sm text-gray-500 bg-gray-50 border-t">
            {t('costs.vehicles.showingFirst', { shown: 20, total: costData.sortedVehicles.length })}
          </div>
        )}
      </div>
    </div>
  );
}

// Trends Tab - Evolution over time
function TrendsTab({ costData, period }) {
  const { t, fmt } = useFormat();
  const currency = costData.currency;
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-amber-600" />
          {t('costs.trends.title')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatusCard
            icon={Euro}
            title={t('costs.trends.currentPeriod')}
            value={`${money(costData.grandTotal, currency)}`}
            status="neutral"
            description={t(`costs.periods.${PERIOD_KEYS[period] || 'thisYear'}`)}
          />
          <StatusCard
            icon={Activity}
            title={t('costs.trends.variation')}
            value={`${costData.trendPercent > 0 ? '+' : ''}${fmt.percent(costData.trendPercent / 100)}`}
            status={costData.trendPercent > 10 ? "danger" : costData.trendPercent < -10 ? "good" : "neutral"}
            description={t('costs.trends.vsPrevious')}
          />
          <StatusCard
            icon={Car}
            title={t('costs.trends.avgCost')}
            value={`${moneyInt(costData.avgPerVehicle, currency)}`}
            status="neutral"
            description={t('costs.trends.perVehicle')}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">{t('costs.trends.byCategory')}</h3>
        <div className="space-y-4">
          {Object.entries(costData.totals)
            .filter(([, value]) => value > 0)
            .sort(([, a], [, b]) => b - a)
            .map(([key, value]) => {
              const percentage = costData.grandTotal > 0 ? (value / costData.grandTotal) * 100 : 0;
              const colors = {
                fuel: 'bg-green-500', maintenance: 'bg-orange-500', claims: 'bg-red-500',
                insurance: 'bg-blue-500', leasing: 'bg-purple-500', tax: 'bg-gray-500', other: 'bg-gray-400'
              };

              return (
                <div key={key}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">{familyLabel(t, key)}</span>
                    <span className="font-medium">{money(value, currency)} ({fmt.percent(percentage / 100)})</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`${colors[key]} h-2 rounded-full transition-all`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

// Loss ratio level colors
const lossRatioColors = {
  ok: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', icon: 'text-green-600' },
  moderate: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: 'text-blue-600' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: 'text-amber-600' },
  high: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', icon: 'text-orange-600' },
  danger: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: 'text-red-600' },
  critical: { bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-900', icon: 'text-red-700' }
};

// Alerts Tab - Loss ratio and high costs
function AlertsTab({ costData }) {
  const { t, fmt } = useFormat();
  const currency = costData.currency;
  const highCostVehicles = costData.sortedVehicles.filter(v => v.total > costData.avgPerVehicle * 1.5);
  const lossRatioAlerts = costData.sortedVehicles.filter(v => v.lossRatio?.alert);
  const fleetLossRatio = costData.fleetLossRatio;

  return (
    <div className="space-y-6">
      {/* Fleet Loss Ratio Banner */}
      {fleetLossRatio && (
        <div className={`rounded-lg p-4 border ${lossRatioColors[fleetLossRatio.level]?.bg || 'bg-gray-50'} ${lossRatioColors[fleetLossRatio.level]?.border || 'border-gray-200'}`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center">
              <Shield className={`w-6 h-6 ${lossRatioColors[fleetLossRatio.level]?.icon || 'text-gray-600'} mr-3`} />
              <div>
                <h3 className={`font-semibold ${lossRatioColors[fleetLossRatio.level]?.text || 'text-gray-800'}`}>
                  {t('costs.alerts.fleetLossRatio', { percent: fmt.percent((fleetLossRatio.percentage || 0) / 100) })}
                </h3>
                {fleetLossRatio.alert && (
                  <p className={`text-sm mt-1 ${lossRatioColors[fleetLossRatio.level]?.text || 'text-gray-600'}`}>
                    {fleetLossRatio.alert}
                  </p>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">{t('costs.alerts.claimsOverPremium')}</p>
              <p className="font-medium">{money(costData.totals?.claims, currency)} / {money(costData.totals?.insurance, currency)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Per-Vehicle Loss Ratio Alerts */}
      {lossRatioAlerts.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 text-amber-600 mr-2" />
            {t('costs.alerts.perVehicleTitle', { count: lossRatioAlerts.length })}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lossRatioAlerts.map(v => {
              const colors = lossRatioColors[v.lossRatio.level] || lossRatioColors.warning;
              return (
                <div key={v.vehicle.id} className={`rounded-lg p-4 border-l-4 ${colors.bg} ${colors.border}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-gray-900">{v.vehicle.license_plate}</p>
                      <p className="text-sm text-gray-500">{v.vehicle.make} {v.vehicle.model}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${colors.text} ${colors.bg}`}>
                      {fmt.percent((v.lossRatio.percentage || 0) / 100)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{v.lossRatio.alert}</p>
                  <div className="text-xs text-gray-500">
                    {t('costs.alerts.claimsAndPremium', { claims: money(v.claims, currency), premium: money(v.insurance, currency) })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* High Cost Vehicles */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center mb-4">
          <AlertCircle className="w-5 h-5 text-amber-600 mr-2" />
          <span className="font-semibold text-gray-900">
            {t('costs.alerts.highCosts', { count: highCostVehicles.length })}
          </span>
        </div>

        {highCostVehicles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {highCostVehicles.map(v => {
              const vsAverage = (v.total - costData.avgPerVehicle) / costData.avgPerVehicle;
              return (
                <div key={v.vehicle.id} className="bg-gray-50 rounded-lg p-4 border-l-4 border-red-500">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-gray-900">{v.vehicle.license_plate}</p>
                      <p className="text-sm text-gray-500">{v.vehicle.make} {v.vehicle.model}</p>
                    </div>
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                      +{fmt.percent(vsAverage, { decimals: 0 })}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-red-600">{money(v.total, currency)}</p>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {[
                        ['fuel', v.fuel],
                        ['maintenance', v.maintenance],
                        ['claims', v.claims],
                        ['other', v.insurance + v.leasing + v.tax + v.other],
                      ].map(([key, amount]) => (
                        <div key={key}>
                          <span className="text-gray-500">{t('costs.alerts.familyValue', { family: familyLabel(t, key) })}</span>
                          <span className="font-medium ml-1">{money(amount, currency)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Activity className="w-12 h-12 text-green-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('costs.alerts.noHighCost')}</p>
          </div>
        )}
      </div>

      {/* No alerts at all */}
      {!fleetLossRatio?.alert && lossRatioAlerts.length === 0 && highCostVehicles.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Activity className="w-16 h-16 text-green-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('costs.alerts.noAlertTitle')}</h3>
          <p className="text-gray-500">{t('costs.alerts.noAlertHint')}</p>
        </div>
      )}
    </div>
  );
}

// ── Group reporting (« Par filiale ») ────────────────────────────────────────
// Cost families, in display order — mirrors the 8 keys of the backend contract.
// Labels: common.costFamilies.<key>
const COST_FAMILY_KEYS = ['fuel', 'consumable', 'maintenance', 'insurance', 'leasing', 'claims', 'tax', 'other'];

// Generic: ISO 3166-1 alpha-2 → regional-indicator flag emoji (no per-country table).
function countryFlag(iso2) {
  const code = String(iso2 || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '🏳️';
  return String.fromCodePoint(...[...code].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
}

// Country name in the UI language from the ISO code — Intl does it, no table.
const countryNamesCache = new Map();
function countryNamesFor(language) {
  const lang = language || 'fr';
  if (!countryNamesCache.has(lang)) {
    let dn = null;
    try {
      dn = typeof Intl !== 'undefined' && Intl.DisplayNames ? new Intl.DisplayNames([lang], { type: 'region' }) : null;
    } catch { dn = null; }
    countryNamesCache.set(lang, dn);
  }
  return countryNamesCache.get(lang);
}
function countryName(iso2, language) {
  const code = String(iso2 || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '—';
  try { return countryNamesFor(language)?.of(code) || code; } catch { return code; }
}

function ShareBar({ value, total }) {
  const { fmt } = useFormat();
  const pct = total > 0 ? Math.max(0, Math.min(100, (value / total) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 bg-gray-200 rounded-full h-2">
        <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-700 w-12 text-right">{fmt.percent(pct / 100)}</span>
    </div>
  );
}

function ByOrgTab({ byOrg, period }) {
  const { t, fmt, language } = useFormat();
  const [groupByCountry, setGroupByCountry] = useState(false);
  const { organizations, byCountry, consolidated, referenceCurrency, isLoading, error } = byOrg;
  const ref = referenceCurrency || 'EUR';
  const periodLabel = t(`costs.periods.${PERIOD_KEYS[period] || 'thisYear'}`);

  if (isLoading) {
    return <SkeletonLoader type="dashboard" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 flex items-center gap-2">
        <AlertCircle className="w-5 h-5" />
        {t('costs.byOrg.loadError', { error })}
      </div>
    );
  }

  const total = consolidated?.total_ref || 0;
  const thClass = 'px-3 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap';
  const tdNum = 'px-3 py-3 text-right text-sm text-gray-700 tabular-nums whitespace-nowrap';

  return (
    <div className="space-y-6">
      {/* Consolidated block */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatusCard
          icon={Globe}
          title={t('costs.byOrg.groupTco')}
          value={formatMoney(total, ref, { compact: true })}
          status="neutral"
          description={t('costs.byOrg.periodConsolidated', { period: periodLabel, currency: ref })}
        />
        <StatusCard
          icon={Building2}
          title={t('costs.byOrg.subsidiaries')}
          value={fmt.number(consolidated?.org_count ?? organizations.length)}
          status="neutral"
          description={t('costs.byOrg.countries', { count: byCountry.length })}
        />
        <StatusCard
          icon={Car}
          title={t('costs.common.vehiclesLabel')}
          value={fmt.number(consolidated?.vehicle_count ?? 0)}
          status="neutral"
          description={t('costs.byOrg.allSubsidiaries')}
        />
        <StatusCard
          icon={Euro}
          title={t('costs.byOrg.avgPerVehicle')}
          value={formatMoney(consolidated?.avg_per_vehicle_ref, ref, { compact: true })}
          status="neutral"
          description={t('costs.byOrg.inCurrency', { currency: ref })}
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-600" />
            {groupByCountry ? t('costs.byOrg.byCountryTitle') : t('costs.byOrg.bySubsidiaryTitle')}
          </h3>
          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-500">
              {groupByCountry
                ? t('costs.byOrg.consolidatedAmounts', { currency: ref })
                : t('costs.byOrg.localFamilies', { currency: ref })}
            </span>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                checked={groupByCountry}
                onChange={e => setGroupByCountry(e.target.checked)}
              />
              {t('costs.byOrg.groupByCountry')}
            </label>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className={`${thClass} text-left`}>{groupByCountry ? t('costs.byOrg.colCountry') : t('costs.byOrg.colSubsidiary')}</th>
                {!groupByCountry && <th className={`${thClass} text-left`}>{t('costs.byOrg.colCountry')}</th>}
                {groupByCountry && <th className={`${thClass} text-right`}>{t('costs.byOrg.subsidiaries')}</th>}
                <th className={`${thClass} text-right`}>{t('costs.common.vehiclesLabel')}</th>
                {COST_FAMILY_KEYS.map(key => (
                  <th key={key} className={`${thClass} text-right`}>{familyLabel(t, key)}</th>
                ))}
                {!groupByCountry && <th className={`${thClass} text-right`}>{t('costs.byOrg.colLocalTco')}</th>}
                <th className={`${thClass} text-right`}>{t('costs.byOrg.colRefTco', { currency: ref })}</th>
                <th className={`${thClass} text-left`}>{t('costs.byOrg.colShare')}</th>
              </tr>
            </thead>
            <tbody>
              {groupByCountry
                ? byCountry.map(row => (
                  <tr key={row.country} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                      <span className="mr-2">{countryFlag(row.country)}</span>{countryName(row.country, language)}
                    </td>
                    <td className={tdNum}>{row.org_count ?? 0}</td>
                    <td className={tdNum}>{row.vehicle_count ?? 0}</td>
                    {COST_FAMILY_KEYS.map(key => (
                      <td key={key} className={tdNum}>{formatMoney(row.breakdown_ref?.[key], ref)}</td>
                    ))}
                    <td className={`${tdNum} font-bold text-gray-900`}>{formatMoney(row.total_ref, ref)}</td>
                    <td className="px-3 py-3"><ShareBar value={row.total_ref || 0} total={total} /></td>
                  </tr>
                ))
                : organizations.map(row => (
                  <tr key={row.org_id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900">{row.name || row.org_id}</p>
                      <p className="text-xs text-gray-500">{row.org_id} · {row.currency}</p>
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap" title={countryName(row.country, language)}>
                      <span className="mr-1">{countryFlag(row.country)}</span>{row.country || '—'}
                    </td>
                    <td className={tdNum}>{row.vehicle_count ?? 0}</td>
                    {COST_FAMILY_KEYS.map(key => (
                      <td key={key} className={tdNum}>{formatMoney(row.breakdown?.[key], row.currency)}</td>
                    ))}
                    <td className={`${tdNum} font-semibold text-gray-900`}>{formatMoney(row.total, row.currency)}</td>
                    <td className={`${tdNum} font-bold text-gray-900`}>{formatMoney(row.total_ref, ref)}</td>
                    <td className="px-3 py-3"><ShareBar value={row.total_ref || 0} total={total} /></td>
                  </tr>
                ))}
              {(groupByCountry ? byCountry : organizations).length === 0 && (
                <tr>
                  <td colSpan={COST_FAMILY_KEYS.length + 6} className="px-4 py-8 text-center text-sm text-gray-500">
                    {t('costs.byOrg.empty')}
                  </td>
                </tr>
              )}
            </tbody>
            {consolidated && (
              <tfoot className="bg-amber-50 border-t-2 border-amber-200">
                <tr>
                  <td className="px-3 py-3 text-sm font-bold text-gray-900 whitespace-nowrap">{t('costs.byOrg.groupTotal')}</td>
                  <td className={`${tdNum} font-medium`}>
                    {groupByCountry ? (consolidated.org_count ?? organizations.length) : t('costs.byOrg.countries', { count: byCountry.length })}
                  </td>
                  <td className={`${tdNum} font-medium`}>{consolidated.vehicle_count ?? 0}</td>
                  {COST_FAMILY_KEYS.map(key => (
                    <td key={key} className={`${tdNum} font-medium`}>{formatMoney(consolidated.breakdown_ref?.[key], ref)}</td>
                  ))}
                  {!groupByCountry && <td className={`${tdNum} text-gray-400`}>—</td>}
                  <td className={`${tdNum} font-bold text-gray-900`}>{formatMoney(total, ref)}</td>
                  <td className="px-3 py-3"><ShareBar value={total} total={total} /></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {!groupByCountry && organizations.length > 0 && (
          <div className="p-3 text-xs text-gray-500 bg-gray-50 border-t">
            {t('costs.byOrg.footnote', { currency: ref })}
          </div>
        )}
      </div>
    </div>
  );
}

// Main Cost Dashboard View
function CostDashboardView({ onMessage }) {
  const { t } = useFormat();
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('month');

  // Group context: the « Par filiale » tab only makes sense with several accessible orgs
  const { accessibleOrgs } = useAuth();
  const isGroup = (accessibleOrgs?.length || 0) > 1;
  const availableTabs = useMemo(
    () => COST_TABS.filter(tab => !tab.groupOnly || isGroup).map(tab => ({ id: tab.key, label: t(`costs.tabs.${TAB_LABEL_KEYS[tab.key]}`), ...tab })),
    [isGroup, t]
  );

  // V3: Tab permission filtering
  const { visibleTabs } = useTabPermissions('costs', availableTabs);

  // Reset activeTab if current tab is hidden by permissions
  useEffect(() => {
    const visibleKeys = visibleTabs.map(tab => tab.id);
    if (visibleKeys.length > 0 && !visibleKeys.includes(activeTab)) {
      setActiveTab(visibleKeys[0]);
    }
  }, [visibleTabs, activeTab]);

  // Use new KPI hooks for server-side cost computation
  const {
    breakdown: totals,
    grandTotal,
    avgPerVehicle,
    vehicleCount,
    fleetLossRatio,
    currency: kpiCurrency,
    isLoading: kpiLoading,
  } = useCostKPIs(period);

  const {
    vehicles: vehicleCostsRaw,
    isLoading: vehiclesLoading,
  } = useCostsByVehicle(period);

  const byOrg = useCostsByOrg(period, { enabled: isGroup });

  const isLoading = kpiLoading || vehiclesLoading;

  // Transform vehicle data to match expected format
  const costData = useMemo(() => {
    // Build vehicleCosts map for backward compatibility
    const vehicleCosts = {};
    const sortedVehicles = (vehicleCostsRaw || []).map(v => {
      const vehicleData = {
        vehicle: {
          id: v.vehicle_id,
          plate: v.plate,
          make: v.brand,
          model: v.model,
          license_plate: v.plate,
          status: v.status,
          mileage: v.mileage || 0
        },
        fuel: v.breakdown?.fuel || 0,
        maintenance: v.breakdown?.maintenance || 0,
        claims: v.breakdown?.claims || 0,
        insurance: v.breakdown?.insurance || 0,
        leasing: v.breakdown?.leasing || 0,
        tax: v.breakdown?.tax || 0,
        other: v.breakdown?.other || 0,
        total: v.total || 0,
        lossRatio: v.lossRatio || null
      };
      vehicleCosts[v.vehicle_id] = vehicleData;
      return vehicleData;
    });

    return {
      vehicleCosts,
      sortedVehicles,
      totals: totals || { fuel: 0, maintenance: 0, claims: 0, insurance: 0, leasing: 0, tax: 0, other: 0 },
      grandTotal: grandTotal || 0,
      avgPerVehicle: avgPerVehicle || 0,
      trendPercent: 0, // TODO-005: Add trend calculation (see docs/TODO_ISSUES.md)
      vehicleCount: vehicleCount || 0,
      fleetLossRatio: fleetLossRatio || null,
      currency: kpiCurrency || 'EUR'
    };
  }, [vehicleCostsRaw, totals, grandTotal, avgPerVehicle, vehicleCount, fleetLossRatio, kpiCurrency]);

  const handleExportCSV = () => {
    const headers = [
      t('costs.export.vehicle'), t('costs.export.plate'),
      t('common.costFamilies.fuel'), t('common.costFamilies.maintenance'), t('common.costFamilies.claims'),
      t('common.costFamilies.insurance'), t('common.costFamilies.leasing'), t('common.costFamilies.tax'), t('common.costFamilies.other'),
      t('costs.export.total'),
    ];
    const rows = costData.sortedVehicles.map(v => [
      `${v.vehicle.make} ${v.vehicle.model}`,
      v.vehicle.license_plate,
      v.fuel.toFixed(2),
      v.maintenance.toFixed(2),
      v.claims.toFixed(2),
      v.insurance.toFixed(2),
      v.leasing.toFixed(2),
      v.tax.toFixed(2),
      v.other.toFixed(2),
      v.total.toFixed(2)
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `costs_${period}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <OverviewTab
            costData={costData}
            period={period}
            setActiveTab={setActiveTab}
            handleExportCSV={handleExportCSV}
          />
        );
      case 'breakdown':
        return <BreakdownTab costData={costData} />;
      case 'vehicles':
        return <VehiclesTab costData={costData} />;
      case 'by_org':
        return <ByOrgTab byOrg={byOrg} period={period} />;
      case 'trends':
        return <TrendsTab costData={costData} period={period} />;
      case 'alerts':
        return <AlertsTab costData={costData} />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return <SkeletonLoader type="dashboard" />;
  }

  // Handle empty data state
  if (costData.vehicleCount === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <Euro className="w-8 h-8 text-amber-600 mr-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('costs.header.emptyTitle')}</h2>
            <p className="text-sm text-gray-500">{t('costs.header.emptySubtitle')}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('costs.header.noVehicleTitle')}</h3>
          <p className="text-gray-500">{t('costs.header.noVehicleHint')}</p>
        </div>
      </div>
    );
  }

  const currency = costData.currency;

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Euro className="w-6 h-6 text-amber-600" />
              {t('costs.header.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{money(costData.grandTotal, currency)}</span> {t('costs.header.totalSuffix')}
              <span className="text-gray-300">|</span>
              <span className="text-blue-600">{t('costs.common.vehicles', { count: costData.vehicleCount })}</span>
              <span className="text-gray-300">|</span>
              <span className="text-amber-600">{t('costs.header.avgPerVehicleShort', { amount: moneyInt(costData.avgPerVehicle, currency) })}</span>
              {costData.totals.fuel > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-green-600">{t('costs.header.fuelAmount', { amount: money(costData.totals.fuel, currency) })}</span>
                </>
              )}
            </p>
          </div>
          {/* Period selector */}
          <div className="flex rounded-lg overflow-hidden border">
            {[
              { key: 'month', label: t('costs.periods.month') },
              { key: 'quarter', label: t('costs.periods.quarter') },
              { key: 'year', label: t('costs.periods.year') }
            ].map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-4 py-2 text-sm ${period === p.key
                    ? 'bg-amber-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tabs — filtered by V3 permissions */}
        <div className="pt-4 border-t border-gray-200">
          <nav className="flex flex-wrap gap-2">
            {availableTabs.filter(tab => visibleTabs.some(vt => vt.id === tab.key)).map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-amber-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content — reporting-only screen (no write action anywhere: CSV export is
          client-side, quick actions only switch tabs), so no ReadOnlyGuard needed.
          A blanket guard here would have blocked consultation itself for an
          observer profile, which is exactly what this mode must not do. */}
      {renderContent()}
    </div>
  );
}

export default CostDashboardView;
